import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { combineLatest, Observable } from "rxjs";
import { filter, first, map, switchMap } from "rxjs/operators";
import { I18nService } from "src/app/i18n/services";
import { DateUtility } from "src/app/interfaces/date";
import { NotificationFacade } from "src/app/notifications/services/notification.facade";
import { CirculationFacade } from "src/app/services/circulation.facade";
import { StepFacade } from "src/app/step/services/step.facade";
import { SubSink } from "subsink";
import { AbstractUserService, IPerson, Step } from "../../../../interfaces";
import { LogClass, Logger, LogMethod } from "../../../../logger";
import { NavigationService } from "../../../../services";
import { STEPTYPE_FORCONFIRMATION } from "../../constants";

@Component({
    selector: "circ-create",
    templateUrl: "./create.component.html",
    styleUrls: ["./create.component.scss"],
    standalone: false
})
@LogClass({ source: "stepForConfirmation/CreateComponent" })
export class CreateComponent implements OnInit, OnDestroy {
  public minDueDate$: Observable<Date>;

  public form: FormGroup;
  public saving = false;

  private logger: Logger;
  private subscriptions = new SubSink();

  constructor(
    private circulationFacade: CirculationFacade,
    private stepFacade: StepFacade,
    private userService: AbstractUserService,
    private navigate: NavigationService,
    private i18nService: I18nService,
    private notification: NotificationFacade
  ) {
    this.logger.debug("ctor");
    this.createForm();
  }

  public abort(): void {
    this.navigateToCurrentCirculation();
  }

  private navigateToCurrentCirculation(): void {
    this.circulationFacade
      .getCurrentCirculation()
      .pipe(
        map((circ) => circ.id),
        first()
      )
      .subscribe((circId) => this.navigate.toCirculation(circId));
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  @LogMethod()
  public ngOnInit(): void {
    const dueDateRange$ = this.circulationFacade.getCurrentCirculation().pipe(
      filter((c) => !!c),
      switchMap((circ) =>
        circ.getValidDueDateRangeForStep(this.circulationFacade, null)
      ),
      map((range) => {
        const from =
          range.from < DateUtility.getTodayMin()
            ? DateUtility.getTodayMin()
            : range.from;
        const to = range.to;
        return { from, to };
      })
    );

    this.minDueDate$ = dueDateRange$.pipe(map((range) => range.from));

    this.i18nService
      .get("step_forConfirmation_new_name")
      .pipe(first())
      .subscribe((x) => {
        this.form.patchValue({
          title: x
        });
      });
  }

  public save(): void {
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      return;
    }

    this.saving = true;
    this.logger.info("saving");

    const logins: string[] = this.form.value.responsible.map(
      (x: IPerson) => x.login
    );

    combineLatest([
      this.circulationFacade.getCurrentCirculation(),
      this.userService.getUsers(logins)
    ])
      .pipe(
        map(
          ([circ, users]) =>
            new Step({
              id: 0,
              stepNo: 0,
              dueDate: DateUtility.middleOfDay(this.form.value.dueDate),
              tasks: {
                firstResponseApplies: this.form.value.firstResponseApplies,
                assignees: users.map((u) => {
                  return {
                    id: 0,
                    user: u
                  };
                })
              },
              circulationId: circ.id,
              status: "Waiting",
              payload: {
                stepType: STEPTYPE_FORCONFIRMATION,
                data: {
                  title: this.form.value.title,
                  comment: this.form.value.comment
                }
              },
              permissions: {
                canAddDocuments: this.form.value.canAddDocuments
              }
            })
        ),
        switchMap((step) => this.stepFacade.save(step)),
        first()
      )
      .subscribe({
        next: (ok) => {
          this.logger.info("saved:" + ok);

          this.navigateToCurrentCirculation();
        },
        error: (err) => {
          this.logger.info("error saving step: " + JSON.stringify(err));

          this.notification.showError(
            "Error while saving this step. Please ask your Administrator for help." +
              err.error.message
          );

          this.navigateToCurrentCirculation();
        }
      });
  }

  private createForm() {
    this.form = new FormGroup({
      title: new FormControl("", [Validators.required]),
      responsible: new FormControl([], [Validators.required]),
      comment: new FormControl(""),
      dueDate: new FormControl(null, [Validators.required]),
      firstResponseApplies: new FormControl(false),
      canAddDocuments: new FormControl(false)
    });
  }
}
